import { IconCopy, IconTrash } from '@tabler/icons-react'
import { FieldType } from 'database'
import {
  Button,
  Separator,
  ScrollArea,
  TooltipProvider,
  TooltipTrigger,
  Tooltip,
  TooltipContent,
} from 'ui'
import { PDField, usePDFStore } from '../../stores/pdf.store'
import { FieldAttributeOptions } from './FieldAttributes'
import { FieldAttributeAlignment } from './FieldAttributes/Alignment'
import { FieldSignatureReference } from './FieldAttributes/FieldSignatureReference'
import { FieldAttributeFormat } from './FieldAttributes/Format'
import { FieldAttributeHelperText } from './FieldAttributes/HelperText'
import { FieldAttributeLabel } from './FieldAttributes/Label'
import {
  FieldValidationRequired,
  FieldValidationSignature,
} from './FieldValidations'
import { FieldValidationLength } from './FieldValidations/Length'
import { FieldValidationRange } from './FieldValidations/Range'
import { FieldValidationRegex } from './FieldValidations/Regex'
import { FieldItemIcon } from './FieldsEditLayer/FieldItem'
import { nanoid } from 'nanoid'
import { toast } from 'sonner'
import { useCallback } from 'react'
import { usePDF } from '../../stores/pdf.store'

export const FIELDS_SETTINGS_PANEL_ID = 'fields-settings-panel'

const fieldTypeComps: {
  [K in FieldType]: Array<FieldValidationSignature>
} = {
  signature_date: [
    FieldAttributeLabel,
    FieldSignatureReference,
    FieldAttributeAlignment,
    FieldAttributeFormat,
  ],
  select: [
    FieldAttributeLabel,
    FieldValidationRequired,
    FieldAttributeFormat,
    FieldAttributeAlignment,
    FieldAttributeOptions,
    FieldAttributeHelperText,
  ],
  person_name: [
    FieldAttributeLabel,
    FieldAttributeFormat,
    FieldAttributeAlignment,
  ],
  company_name: [
    FieldAttributeLabel,
    FieldValidationRequired,
    FieldAttributeFormat,
    FieldAttributeAlignment,
  ],
  signature: [FieldAttributeLabel],
  checkbox: [FieldAttributeLabel, FieldAttributeHelperText],
  email: [FieldAttributeLabel, FieldAttributeFormat, FieldAttributeAlignment],
  text: [
    FieldAttributeLabel,
    FieldAttributeFormat,
    FieldAttributeAlignment,
    FieldValidationLength,
    FieldValidationRegex,
    FieldAttributeHelperText,
    FieldValidationRequired,
  ],
  textarea: [
    FieldAttributeLabel,
    FieldAttributeFormat,
    FieldAttributeAlignment,
    FieldValidationLength,
    FieldValidationRegex,
    FieldAttributeHelperText,
    FieldValidationRequired,
  ],
  initials: [
    FieldAttributeLabel,
    FieldAttributeAlignment,
    FieldValidationRequired,
  ],
  number: [
    FieldAttributeLabel,
    FieldAttributeFormat,
    FieldAttributeHelperText,
    FieldAttributeAlignment,
    FieldValidationRange,
    FieldValidationRequired,
  ],
}

export const FieldSettingsPanel = ({}: {}) => {
  const currentField = usePDFStore(
    (state) => state.fields.find((f) => f.id === state.currentEditingFieldId!)!
  )

  const removeField = usePDFStore((state) => state.removeField)

  const fieldComps = fieldTypeComps[currentField.type]

  return (
    <ScrollArea
      id={FIELDS_SETTINGS_PANEL_ID}
      className='overflow-y-auto h-[calc(100vh-7rem)] [&>div>div]:!block shrink-0'
    >
      <div className='p-5 space-y-4'>
        <div className='flex flex-row items-center space-x-2'>
          <FieldItemIcon className='w-6 h-6' type={currentField.type} />
          <p>{currentField.label}</p>
        </div>
        <div className='space-y-8'>
          <div className='space-y-4'>
            {fieldComps.map((FieldTypeComp, index) => (
              <FieldTypeComp key={index} field={currentField} />
            ))}
          </div>
          <DuplicateFieldButton />
          <Separator />
          <Button
            onClick={() => removeField(currentField.id)}
            size='xs'
            variant={'outline-secondary'}
          >
            <IconTrash className='w-4 h-4 mr-2' />
            Eliminar campo
          </Button>
        </div>
      </div>
    </ScrollArea>
  )
}

export const DuplicateFieldButton = ({
  fieldId,
  minimized = false,
}: {
  fieldId?: string
  minimized?: boolean
}) => {
  const currentField = usePDFStore(
    (state) =>
      state.fields.find(
        (f) => f.id === fieldId || f.id === state.currentEditingFieldId!
      )!
  )
  const documents = usePDFStore((state) => state.documents)
  const fields = usePDFStore((state) => state.fields)
  const addBatchFields = usePDFStore((state) => state.addBatchFields)

  const handleDuplicate = useCallback(() => {
    const currentDocIndex = documents.findIndex(
      (doc) => doc.id === currentField.document_id
    )
    const newFields: PDField[] = []

    documents.slice(currentDocIndex).forEach((doc) => {
      const startPage =
        doc.id === currentField.document_id ? currentField.page + 1 : 0

      for (let page = startPage; page < doc.pages.length; page++) {
        const existingField = fields.find(
          (f) =>
            f.document_id === doc.id &&
            f.page === page &&
            f.label === currentField.label &&
            f.type === currentField.type &&
            f.recipient_id === currentField.recipient_id
        )

        if (!existingField) {
          newFields.push({
            ...currentField,
            id: nanoid(),
            external_id: nanoid(),
            document_id: doc.id,
            page: page,
          })
        }
      }
    })

    if (newFields.length > 0) {
      addBatchFields(newFields)
      toast.success(`Se replicó el campo exitosamente`)
    }
  }, [currentField, documents, fields, addBatchFields])

  if (minimized) {
    return (
      <Button size='xs' variant={'outline-secondary'} onClick={handleDuplicate}>
        <IconCopy className='w-3 h-3' />
      </Button>
    )
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            size='xs'
            variant={'outline-secondary'}
            onClick={handleDuplicate}
          >
            <IconCopy className='w-4 h-4 mr-2' />
            Replicar
          </Button>
        </TooltipTrigger>
        <TooltipContent side='left'>
          <p className='text-xs'>
            Copia y pega el campo en las páginas siguientes
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
